import { QueryClient } from '@tanstack/react-query';

import { ApiError } from './api/client';

/**
 * The one query cache for the console.
 *
 * It lives here rather than in `main.tsx` so that signing out can empty it.
 * Everything cached — members, bills, complaints, the dashboard — belongs to
 * the society that was signed in. Leaving it in place meant the next person to
 * sign in saw the previous society's data until each query happened to refetch,
 * which on a tenanted product is a data leak, not a stale screen.
 *
 * The defaults encode two product decisions:
 *   - Never retry a 4xx. A 402 plan gate or a 403 fails identically three times
 *     and only delays the message the user needs to see.
 *   - Refetch on window focus. An admin leaves the complaints board open all
 *     day; stale counts there are worse than an extra request.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: true,
      retry: (failureCount, error) => {
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
        return failureCount < 2;
      },
    },
    mutations: {
      // A mutation that failed may well have landed; replaying it blindly is how
      // a resident gets charged twice.
      retry: false,
    },
  },
});

/** Empties every cached response. Called when the signed-in identity changes. */
export function clearQueryCache(): void {
  queryClient.clear();
}
